// @soft-depth
import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  Award,
  BadgeCheck,
  Calculator,
  ChartColumn,
  Check,
  Clock,
  Compass,
  Crown,
  FileText,
  GraduationCap,
  Headset,
  Heart,
  HeartHandshake,
  MessageCircle,
  PartyPopper,
  Scale,
  ShieldCheck,
  Target,
  TrendingUp,
  Trophy,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { COVER_ANIMALS } from '../../../views/CoverQuiz/coverQuizData';
import SoftDepthScope from '../../../design-system/SoftDepthScope';

/**
 * DISC 對話建議 / DISC 溝通手冊, laid out to be scanned in the minute before a
 * meeting rather than read: the opening question as a speech bubble, 他想要 as
 * keyword pills, 切入角度 as icon chips, and 說法 / 避忌 side by side as a do /
 * don't pair.
 *
 * Shared by 招募 IDEA (`mode="recruit"`, copy `clientv2-recruit-disc-<letter>-<row>`)
 * and Smart IDEA's 溝通方式 (`mode="business"`, copy
 * `disc-playbook-business-<letter>-<row>`), and the ClientV2 drawer's
 * 招募對話：如何開口 dialog. 他想要 is split on 、/, and 切入角度 on · — the
 * separators every locale uses — so each piece gets its own pill or chip.
 *
 * Soft Depth, always business mode: `mode` picks the copy, never the colours
 * (IDEA stays blue in 招募, as before the redesign). 他想要 keeps the type's own hue (COVER_ANIMALS, as on the type chip in the card head).
 */

export type DiscTipsMode = 'recruit' | 'business';
type DiscLetter = 'D' | 'I' | 'S' | 'C';
type Row = 'ask' | 'wants' | 'angle' | 'do' | 'dont';

type IntlLike = { formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string };

const VALUE_ID: Record<DiscTipsMode, (letter: DiscLetter, row: Row) => string> = {
  recruit: (letter, row) => `clientv2-recruit-disc-${letter}-${row === 'do' ? 'how' : row === 'dont' ? 'avoid' : row}`,
  business: (letter, row) => `disc-playbook-business-${letter}-${row === 'do' ? 'how' : row === 'dont' ? 'avoid' : row}`
};

// 招募 has its own 這樣說 / 避免 labels; Smart IDEA keeps its 怎樣講 / 避免.
const LABEL_ID: Record<DiscTipsMode, (row: Row) => string> = {
  recruit: row => `clientv2-recruit-disc-${row}`,
  business: row => `disc-playbook-business-${row === 'do' ? 'how' : row === 'dont' ? 'avoid' : row}`
};

// One icon per 切入角度, in the order the copy lists them.
const ANGLE_ICONS: Record<DiscTipsMode, Record<DiscLetter, LucideIcon[]>> = {
  recruit: {
    D: [Crown, TrendingUp, Users],
    I: [Trophy, UserPlus, PartyPopper],
    S: [Clock, GraduationCap, HeartHandshake],
    C: [Calculator, ChartColumn, BadgeCheck]
  },
  business: {
    D: [Calculator, Scale],
    I: [MessageCircle, Heart],
    S: [ShieldCheck, Headset],
    C: [FileText, ChartColumn]
  }
};

const splitList = (text: string, separator: RegExp) =>
  text
    .split(separator)
    .map(part => part.trim())
    .filter(Boolean);

export type DiscTipsProps = {
  mode: DiscTipsMode;
  letter: DiscLetter;
  intl: IntlLike;
  /** Show 開場問題 as a bubble, last. The IDEA dialogs leave it out: their own opener closes the card. */
  showAsk?: boolean;
  testId?: string;
};

/** A row's small label: icon + word, muted unless the row says otherwise. */
const Label = (props: { icon?: React.ReactNode; children: React.ReactNode; color?: string }) => (
  <Typography
    component="span"
    variant="subtitle2"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mb: 0.75,
      fontWeight: 700,
      color: props.color || 'text.secondary'
    }}
  >
    {props.icon}
    {props.children}
  </Typography>
);

const DiscTips = ({ mode, letter, intl, showAsk = false, testId = 'disc-tips' }: DiscTipsProps) => {
  const t = (id: string) => intl.formatMessage({ id });
  const value = (row: Row) => t(VALUE_ID[mode](letter, row));
  const label = (row: Row) => t(LABEL_ID[mode](row));
  const wants = splitList(value('wants'), /[、,，]/);
  const angles = splitList(value('angle'), /\s·\s/);
  const icons = ANGLE_ICONS[mode][letter] || [];
  const hue = COVER_ANIMALS[letter];

  // 這樣說 / 避免 — a do / don't pair, in the status colours they mean.
  const pairBox = (row: 'do' | 'dont', tone: 'success' | 'error', icon: React.ReactNode) => (
    <Box
      data-row={row === 'do' ? 'how' : 'avoid'}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: `${tone}.border`,
        bgcolor: `${tone}.soft`,
        color: `${tone}.dark`
      }}
    >
      <Label icon={icon} color="inherit">
        {label(row)}
      </Label>
      <Typography component="p" variant="body2" sx={{ m: 0 }}>
        {value(row)}
      </Typography>
    </Box>
  );

  return (
    <SoftDepthScope mode="business">
      <Stack data-testid={testId} data-mode={mode} data-letter={letter} sx={{ gap: 1.75 }}>
        <div data-row="wants">
          <Label icon={<Target size={13} aria-hidden="true" />}>{label('wants')}</Label>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {wants.map(want => (
              <Chip
                key={want}
                label={want}
                data-testid="disc-tips-want"
                sx={{ bgcolor: hue?.bg, color: hue?.color, borderColor: hue?.bg }}
              />
            ))}
          </Stack>
        </div>

        <div data-row="angle">
          <Label icon={<Compass size={13} aria-hidden="true" />}>{label('angle')}</Label>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {angles.map((angle, i) => {
              const Icon = icons[i] || Award;
              return (
                <Chip
                  key={angle}
                  variant="outlined"
                  label={angle}
                  data-testid="disc-tips-angle"
                  icon={<Icon size={13} aria-hidden="true" />}
                  sx={{
                    height: 'auto',
                    minHeight: 28,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    fontWeight: 500,
                    // An angle can run longer than a phone's width: wrap, never ellipsize.
                    '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                    '& .MuiChip-icon': { color: 'text.secondary', ml: 1, mr: -0.5 }
                  }}
                />
              );
            })}
          </Stack>
        </div>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1 }}>
          {pairBox('do', 'success', <Check size={13} aria-hidden="true" />)}
          {pairBox('dont', 'error', <X size={13} aria-hidden="true" />)}
        </Box>

        {/* The line to say comes last, once the adviser has read who they are talking to. */}
        {showAsk && (
          <Stack
            direction="row"
            data-row="ask"
            sx={{ alignItems: 'flex-start', gap: 1.25, pt: 1.75, borderTop: '1px dashed', borderColor: 'divider' }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                mt: '2px',
                borderRadius: '50%',
                bgcolor: 'primary.soft',
                color: 'primary.dark'
              }}
            >
              <MessageCircle size={16} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Label color="primary.dark">{label('ask')}</Label>
              {/* 招募's 開場問題 is three questions, one per line. */}
              <Typography
                component="p"
                variant="body1"
                sx={{
                  m: 0,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '4px 12px 12px 12px',
                  bgcolor: 'primary.soft',
                  color: 'primary.dark',
                  fontWeight: 600,
                  whiteSpace: 'pre-line'
                }}
              >
                {value('ask')}
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>
    </SoftDepthScope>
  );
};

export default DiscTips;
